import { OrganizationProgressDto } from "../../../common/service/server/controller/OrganizationProgressController";
import { OrganizationDataRecipientDto } from "../../../common/service/server/dto/OrganizationDataRecipientDto";
import { OrganizationDto } from "../../../common/service/server/dto/OrganizationDto";
import { PrivacyCenterDto } from "../../../common/service/server/dto/PrivacyCenterDto";
import { Routes } from "../../root/routes";
import { GetCompliantMajorStep, GetCompliantStep } from "./GetCompliantSteps";
import { isGoogleAnalytics } from "../../util/vendors";

export type StepLogicContext = {
  org: OrganizationDto;
  recipients: OrganizationDataRecipientDto[];
  orgProgress: OrganizationProgressDto[];
  privacyCenter?: PrivacyCenterDto;
};

export const getStepLogic = (step: GetCompliantStep): StepLogic => {
  const logic: Logic = STEP_LOGIC[step];
  const route = logic?.route || Routes.getCompliant.root;

  return {
    visible: logic?.visible || DefaultStepLogic.visible,
    skip: logic?.skip || DefaultStepLogic.skip,
    label: logic?.label || "",
    majorStep: logic?.majorStep,
    minorStep: logic?.minorStep,
    route,
    matchesRoute: logic?.matchesRoute || ((path) => path == route),
  };
};

interface StepLogic {
  majorStep: GetCompliantMajorStep;
  minorStep?: string;
  visible: (context: StepLogicContext) => boolean;
  skip: (context: StepLogicContext) => boolean;
  matchesRoute: (path: string) => boolean;
  label: string;
  route: string;
}

interface Logic {
  visible?: (context: StepLogicContext) => boolean;
  skip?: (context: StepLogicContext) => boolean;
  matchesRoute?: (path: string) => boolean;
  label: string;
  route: string;
  majorStep: GetCompliantMajorStep;
  minorStep?: string;
}

const DefaultStepLogic: Logic = {
  visible: (_context: StepLogicContext) => true,
  skip: (_context: StepLogicContext) => false,
  matchesRoute: (path: string) => path == Routes.getCompliant.root,
  majorStep: "Done",
  label: "NO LABEL",
  route: Routes.getCompliant.root,
};

const BusinessSurvey: Logic = {
  label: "Business Survey",
  route: Routes.getCompliant.surveyAnswersReview,
  majorStep: "BusinessSurvey",
};

const DataCollection: Logic = {
  label: "Collection",
  route: Routes.getCompliant.businessSurvey.consumerDataCollection,
  majorStep: "ConsumerDataMap",
  matchesRoute: (p) =>
    p == Routes.getCompliant.businessSurvey.consumerDataCollection ||
    p.startsWith(Routes.getCompliant.businessSurvey.consumerDataCollection),
};

const DataMapIntroduction: Logic = {
  label: "Data Map Introduction",
  skip: ({ orgProgress }) => {
    const introProgress = orgProgress?.find((p) => p.key === "data_map_intro");

    if (introProgress && introProgress.progress.value === "DONE") {
      return true;
    }

    return false;
  },
  visible: () => false,
  route: Routes.getCompliant.dataMap.introduction,
  majorStep: "ConsumerDataMap",
};

const AddDataRecipients: Logic = {
  label: "Vendors",
  route: Routes.getCompliant.dataRecipients.root,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const Shopify: Logic = {
  label: "Shopify Store Apps",
  route: Routes.getCompliant.dataRecipients.platforms,
  // POLARIS-2162 - skip the Shopify Apps page during on-boarding (for now)
  skip: (context) => true || !context.recipients.some((p) => p.isPlatform),
  visible: () => false,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const VendorAssociation: Logic = {
  label: "Associations",
  route: Routes.getCompliant.dataRecipients.vendorAssociation,
  visible: (context) => context.org.requiresAssociations,
  matchesRoute: (p) =>
    p == Routes.getCompliant.dataRecipients.vendorAssociation ||
    p.startsWith(Routes.getCompliant.dataRecipients.vendorAssociation),
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const InternalStorage: Logic = {
  label: "Internal Storage",
  route: Routes.getCompliant.dataRecipients.internalStorage,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const DataDisclosures: Logic = {
  label: "Disclosures",
  route: Routes.getCompliant.dataRecipients.dataDisclosures,
  majorStep: "ConsumerDataMap",
  matchesRoute: (p) => {
    return p == Routes.getCompliant.dataRecipients.dataDisclosures;
  },
};

const DataSources: Logic = {
  label: "Sources",
  route: Routes.getCompliant.dataMap.consumerSources,
  majorStep: "ConsumerDataMap",
};

const Contractors: Logic = {
  label: "Contractors",
  route: Routes.getCompliant.dataRecipients.contractors,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const ThirdParty: Logic = {
  label: "Other Parties",
  route: Routes.getCompliant.dataRecipients.thirdPartyRecipients,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const CookieScan: Logic = {
  label: "Cookie Scanner",
  route: Routes.getCompliant.dataRetention.gdprCookieScanner,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const ProcessingRegion: Logic = {
  label: "Regions",
  route: Routes.getCompliant.dataRecipients.processingRegion,
  visible: ({ org }) => org.featureGdpr || org.featurePipeda,
  skip: ({ org }) => !org.featureGdpr && !org.featurePipeda,
  majorStep: "ConsumerDataMap",
  minorStep: "Recipients",
};

const DataSharing: Logic = {
  label: "Targeted Advertising",
  route: Routes.getCompliant.dataRecipients.sharing,
  majorStep: "SharingSelling",
};

const DataSelling: Logic = {
  label: "Data Selling",
  route: Routes.getCompliant.dataRecipients.selling,
  majorStep: "SharingSelling",
};

const CollectionPurposes: Logic = {
  label: "Purposes",
  route: Routes.getCompliant.dataCollection.consumerPurposes,
  majorStep: "DataUses",
};

const ConsumerMinimization: Logic = {
  label: "Minimization",
  route: Routes.getCompliant.dataCollection.corePrivacyConcepts,
  majorStep: "DataUses",
  minorStep: "",
};

const ConsumerLawfulBases: Logic = {
  label: "Lawful Bases",
  route: Routes.getCompliant.dataCollection.gdprLawfulBases,
  skip: ({ org }) => !org.featureGdpr,
  visible: ({ org }) => org.featureGdpr,
  majorStep: "DataUses",
  matchesRoute: (p) => p == Routes.getCompliant.dataCollection.gdprLawfulBases,
};

const InternationalDataTransfers: Logic = {
  label: "Data Transfers",
  route: Routes.getCompliant.dataCollection.internationalDataTransfers,
  skip: ({ org }) => !org.featureGdpr,
  visible: ({ org }) => org.featureGdpr,
  majorStep: "DataUses",
  matchesRoute: (p) =>
    p == Routes.getCompliant.dataCollection.internationalDataTransfers ||
    p == Routes.getCompliant.dataCollection.contactProcessors,
};

const VendorIntegrations: Logic = {
  label: "API Integrations",
  skip: (context) => {
    return !context.recipients.some((dr) => isGoogleAnalytics(dr));
  },
  route: Routes.getCompliant.dataRetention.vendorIntegrations,
  majorStep: "DataRetention",
};

const VerifySubdomain: Logic = {
  label: "Verify Subdomain",
  route: Routes.getCompliant.privacyCenter.verifySubdomain,
  majorStep: "DataRetention",
  skip: (context) => (context.org.domainVerifyComplete ? true : false),
  visible: (context) => (context.org.domainVerifyComplete ? false : true),
};

const Complete: Logic = {
  label: "Schedule Training",
  route: Routes.getCompliant.complete.root,
  majorStep: "Done",
};

type StepMap = Record<GetCompliantStep, Logic>;
const STEP_LOGIC: StepMap = {
  "BusinessSurvey.BusinessSurvey": BusinessSurvey,
  "DataMap.Introduction": DataMapIntroduction,
  "BusinessSurvey.DataCollection": DataCollection,
  "DataRecipients.AddDataRecipients": AddDataRecipients,
  "DataRecipients.Shopify": Shopify,
  "DataRecipients.DataDisclosures": DataDisclosures,
  "DataRecipients.DataSources": DataSources,
  "DataRecipients.InternalStorage": InternalStorage,
  "DataRecipients.Contractors": Contractors,
  "DataRecipients.ThirdPartyRecipients": ThirdParty,
  "DataRecipients.ProcessingRegion": ProcessingRegion,
  "DataRecipients.VendorAssociation": VendorAssociation,
  "DataRecipients.DataSharing": DataSharing,
  "DataRecipients.DataSelling": DataSelling,
  "ConsumerCollection.CollectionPurposes": CollectionPurposes,
  "ConsumerCollection.Minimization": ConsumerMinimization,
  "ConsumerCollection.LawfulBases": ConsumerLawfulBases,
  "ConsumerCollection.InternationalDataTransfers": InternationalDataTransfers,
  "DataRetention.VendorIntegrations": VendorIntegrations,
  "DataRetention.VerifySubdomain": VerifySubdomain,
  "DataRecipients.CookieScan": CookieScan,
  Complete: Complete,
};
